<script setup lang="ts">
import {defineAsyncComponent, onMounted, ref} from 'vue'
import {MarkerData} from '../interfaces/MapsViewInterface'
import mapMarkerImgUrl from '../assets/MapMarker.png'
import {getTranslation, getSettings} from '@/ts/utilities.ts'

interface Props {
	apiKey: String
}

const settings = getSettings()

declare global {
	interface Window {
		initMap: () => void
	}
}

declare let Cookiebot: any

const props = defineProps<Props>()

const mapLoaded = ref(false)

const mapsData = {
	apiKey: props.apiKey,
}

const mapContainer = ref(null)
let map = null
let dealers: MarkerData[] = ref([])
let infoWindows: object[] = ref([])

const viewShopText = getTranslation('FindShopCTA')
const openingHourstext = getTranslation('FindShopOpeningHoursHeader')

const initMap = async () => {
	const { Map, InfoWindow } = await google.maps.importLibrary('maps') as google.maps.MapsLibrary
	const { AdvancedMarkerElement } = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary

	const markersData: MarkerData[] = dealers.value
	let zoom = 15
	if (markersData.length > 3) {
		zoom = 7
	}
	const center: google.maps.LatLngLiteral = {lat: 55.977675, lng: 10.933507}

	map = new Map(mapContainer.value, {
		center,
		zoom,
		disableDefaultUI: true,
		mapId: 'FindShop'
	})

	markersData.forEach((dealer: MarkerData) => {
		let contentString = `
				<div class="flex flex-col gap-y-4 p-2 min-w-60 text-sm">
					<div class="p-4 absolute -right-6 -top-6 bg-white rounded-full shadow cursor-pointer w-12 h-12 flex justify-center items-center">
						<i class="fal fa-times text-black text-lg"></i>
					</div>
					<address class="not-italic">
						<p class="text-lg">${dealer.ShopName}</p>
						${ dealer.Address }<br>
						${ dealer.PostalAndCity }<br>
						${ dealer.Phone }<br>
						<a href="mailto:${dealer.Email}" class="text-black underline">
							${ dealer.Email }
						</a>
					</address>
					<strong>${openingHourstext}</strong>

					<div class="max-w-[10rem]">${dealer.OpeningHours}</div>
					<div class="flex">
						<span class="cursor-pointer rounded-full hover:no-underline focus:no-underline rounded-full bg-primary text-white hover:bg-primary-dark active:border-primary-darker px-6 py-2 flex items-center justify-center gap-2 font-bold disabled:opacity-25 js-shop-link" data-dealer="${dealer.MenuText}">
							${viewShopText}
						</span>
					</div>
				</div>
				`

		let infowindow = new InfoWindow({
			content: contentString
		})

		infoWindows.value.push(infowindow)

		let latLng = {lat: dealer.Lat, lng: dealer.Lng}
		const image = document.createElement('img')
		image.src = mapMarkerImgUrl


		let marker = new AdvancedMarkerElement({
			map,
			position: latLng,
			content: image
		})
		marker.addListener('click', function () {
			for (let i = 0; i < infoWindows.value.length; i++) {
				infoWindows.value[i].close()
			}
			infowindow.open(map, marker)
		})
	})
	let bounds = new google.maps.LatLngBounds()

	if(dealers.value.length > 1) {
		dealers.value.forEach((dealer: MarkerData) => {
			bounds.extend({lat: dealer.Lat, lng: dealer.Lng})
		})
		map.fitBounds(bounds)
	} else {
		map.setCenter({lat: dealers.value[0].Lat, lng: dealers.value[0].Lng})
	}
}

const getDealers = async () => {
	return settings?.shopInfos;
}

const loadMap = () => {
	const googleMapsScript = document.createElement('script')
	googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${mapsData.apiKey}&callback=initMap`
	googleMapsScript.async = true
	window.initMap = initMap
	document.head.appendChild(googleMapsScript)

	mapLoaded.value = true
}

onMounted(() => {
	getDealers().then((markersData: MarkerData[]) => {
		if (!markersData) return

		if(markersData.length > 0) {
			markersData.forEach((markerData: MarkerData) => {
				dealers.value.push(markerData)
			})
		}
		if (Cookiebot.consent.marketing && Cookiebot.consent.statistics) {
			loadMap()
		} else {
			window.addEventListener('CookiebotOnAccept', function () {
				if (Cookiebot.consent.marketing && Cookiebot.consent.statistics) {
					loadMap()
				}
			}, false)
		}
	})
})

const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))
const renewCookies = () => {
	Cookiebot.renew()
}


</script>

<template>
	<div id="maps">
		<div>
			<div
				ref="mapContainer"
				class="block md:min-h-[282px] all-shops cookieconsent-optin-statistics cookieconsent-optin-marketing">
			</div>
			<div
				v-if="!mapLoaded"
				class="flex flex-col mb-5 p-5 bg-warning cookieconsent-optout-statistics cookieconsent-optout-marketing">
				<div class="p-2 text-center">{{ getTranslation('BlockedContentAllowCookiesText') }}</div>
				<div class="p-2 text-center">
					<RenderAction
						action="button"
						theme="dark"
						size="sm"
						@click="renewCookies">
						{{ getTranslation('ChangeCookieSettingsButtonText') }}
					</RenderAction>
				</div>
			</div>
		</div>
	</div>
</template>
